/**
 * The theme switch: Light · System · Dark.
 *
 * A segmented control rather than a single cycling icon, for the same reason
 * the language switch is one (see kiosk/LanguageSwitch): the three states are
 * shown at once, the current one is legible without a tooltip, and reaching any
 * of them is a single click. 'System' is a first-class option, not an absence —
 * it is what lets a machine that flips to dark at night carry the app with it.
 *
 * The CSS does all the actual theming; this only decides what sits on the
 * <html> data-theme attribute (see lib/theme).
 */

import { useEffect, useState } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'
import { applyTheme, getStoredTheme, type ThemeChoice } from '@/lib/theme'
import { cn } from '@/lib/utils'

const OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'dark', label: 'Dark', icon: Moon },
]

export function ThemeToggle({ className }: { className?: string }) {
  // Seed from what the pre-paint bootstrap already applied, so the highlighted
  // segment matches the screen on the very first render.
  const [choice, setChoice] = useState<ThemeChoice>(getStoredTheme)

  const select = (next: ThemeChoice) => {
    setChoice(next)
    applyTheme(next)
  }

  // While on 'system', follow the OS if it changes mid-session — otherwise the
  // media query updates the colours but nothing here would need to; there is no
  // state to sync because 'system' means "no attribute". This effect exists only
  // to keep the control honest if we later show which resolved theme is active.
  useEffect(() => {
    if (choice !== 'system') return
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const noop = () => {}
    media.addEventListener('change', noop)
    return () => media.removeEventListener('change', noop)
  }, [choice])

  return (
    <div
      role="group"
      aria-label="Colour theme"
      className={cn(
        'inline-flex items-center gap-0.5 rounded-full border border-[var(--border-strong)] bg-[var(--surface)] p-0.5',
        className,
      )}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = choice === value
        return (
          <button
            key={value}
            type="button"
            onClick={() => select(value)}
            aria-pressed={active}
            title={label}
            className={cn(
              'grid h-7 w-7 place-items-center rounded-full transition-colors',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand)]',
              active
                ? 'bg-[var(--brand-subtle)] text-[var(--brand)]'
                : 'text-[var(--text-muted)] hover:text-[var(--text)]',
            )}
          >
            <Icon className="h-4 w-4" aria-hidden />
            <span className="sr-only">{label}</span>
          </button>
        )
      })}
    </div>
  )
}
